import { arTranslations } from '@/core/constants/translations/ar';
import { enTranslations } from '@/core/constants/translations/en';
import { logger } from '@/core/logger';
import { asyncStorage } from '@/core/storage';
import type { TranslationKeys } from '@/core/types/i18n';
import { fetchAllNamespaces } from './contentfulClient';
import { normalizeLocaleNamespace } from './localeNamespace';
import {
  applyEmiratesIdOtpResendFromLocalesRaw,
  restoreEmiratesIdOtpResendFromCache,
} from './emiratesIdOtpResendConfigService';
import {
  applyWebScalingFromLocalesRaw,
  restoreWebScalingFromCache,
} from './webScalingConfigService';
import {
  fetchAdhaTranslations,
  fetchAdhaReservationStatusTranslations,
} from './adhaTranslationService';
import { useAppStore } from '@/core/stores/appStore';
import { Platform } from 'react-native';

type Locale = 'en' | 'ar';

// Shorter TTL in development for faster Contentful updates during testing
const isDevelopment = __DEV__ || process.env.NODE_ENV === 'development';
const DEFAULT_TTL_MS = isDevelopment ? 30_000 : 86_400_000; // 30sec in dev, 24hr in prod
// In development, always use 30 seconds unless explicitly overridden with a smaller value
// This ensures Contentful updates are visible quickly during testing
const envTtl = process.env.EXPO_PUBLIC_I18N_CACHE_TTL_MS ? Number(process.env.EXPO_PUBLIC_I18N_CACHE_TTL_MS) : null;
const CACHE_TTL_MS = isDevelopment && (!envTtl || envTtl >= DEFAULT_TTL_MS)
  ? DEFAULT_TTL_MS
  : (envTtl || DEFAULT_TTL_MS);

interface CacheRecord {
  etag?: string;
  updatedAt: number;
  data: TranslationKeys;
  /** From locales entry `enableWebScaling` (same Contentful fetch as translations). */
  enableWebScaling?: boolean;
  /** From locales auth entry `emiratesIdOtpResendSeconds`. */
  emiratesIdOtpResendSeconds?: number;
}

const memoryCache = new Map<Locale, TranslationKeys>();
const metaCache = new Map<Locale, { etag?: string; updatedAt: number }>();
/** Locales whose `adhaReservationStatus` Contentful fetch has settled. */
const reservationStatusLoadedLocales = new Set<Locale>();
const reservationStatusLoads = new Map<Locale, Promise<AdhaTranslationTreeResult>>();

/** Bumps when memoryCache changes so React hooks can re-resolve `t()`. */
let translationRevision = 0;
const translationRevisionListeners = new Set<() => void>();

/**
 * True after the ADHA `adhaReservationStatus` Contentful fetch settles
 * (success or failure). Status messages stay on shimmer until then; after
 * settle they use CF status, or the raw `current_status` when it does not match.
 */
let adhaReservationStatusCopyReady = false;

function bumpTranslationRevision(): void {
  translationRevision += 1;
  translationRevisionListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // Ignore listener errors so one bad subscriber cannot block others
    }
  });
}

function markAdhaReservationStatusCopyPending(): void {
  if (!adhaReservationStatusCopyReady) {
    return;
  }
  adhaReservationStatusCopyReady = false;
  bumpTranslationRevision();
}

function markAdhaReservationStatusCopyReady(): void {
  adhaReservationStatusCopyReady = true;
  bumpTranslationRevision();
}

/** Whether ADHA reservation-status Contentful locales have finished loading. */
export function isAdhaReservationStatusCopyReady(): boolean {
  return adhaReservationStatusCopyReady;
}

type AdhaTranslationTreeResult = Awaited<
  ReturnType<typeof fetchAdhaReservationStatusTranslations>
>;

async function fetchAdhaReservationStatusTranslationsSafe(
  locale: Locale,
  context: string,
): Promise<AdhaTranslationTreeResult> {
  try {
    return await fetchAdhaReservationStatusTranslations(locale);
  } catch (error) {
    logger.warn('translationService', context, { locale, error });
    return {} as AdhaTranslationTreeResult;
  }
}

/** One in-flight Contentful call per locale for the `adhaReservationStatus` tag. */
function loadReservationStatusTranslations(
  locale: Locale,
): Promise<AdhaTranslationTreeResult> {
  const inFlight = reservationStatusLoads.get(locale);
  if (inFlight) {
    return inFlight;
  }
  const load = fetchAdhaReservationStatusTranslationsSafe(
    locale,
    'reservationStatus locales failed',
  ).finally(() => {
    reservationStatusLoads.delete(locale);
  });
  reservationStatusLoads.set(locale, load);
  return load;
}

async function mergeReservationStatusTranslations(
  locale: Locale,
  base: TranslationKeys,
): Promise<TranslationKeys> {
  const reservationStatusTranslations =
    await loadReservationStatusTranslations(locale);
  reservationStatusLoadedLocales.add(locale);
  markAdhaReservationStatusCopyReady();
  return deepMerge(base, reservationStatusTranslations);
}

async function mergeAdhaJourneyTranslations(
  locale: Locale,
  base: TranslationKeys,
): Promise<TranslationKeys> {
  const [adhaTranslations, reservationStatusTranslations] = await Promise.all([
    fetchAdhaTranslations(locale),
    fetchAdhaReservationStatusTranslationsSafe(
      locale,
      'ADHA reservationStatus locales failed',
    ),
  ]);
  return deepMerge(
    deepMerge(base, adhaTranslations),
    reservationStatusTranslations,
  );
}

export function getTranslationRevision(): number {
  return translationRevision;
}

export function subscribeTranslationRevision(listener: () => void): () => void {
  translationRevisionListeners.add(listener);
  return () => {
    translationRevisionListeners.delete(listener);
  };
}

function bundled(locale: Locale): TranslationKeys {
  return locale === 'ar' ? arTranslations : enTranslations;
}

function deepMerge<T>(base: T, override: Partial<T>): T {
  const result: any = Array.isArray(base) ? [...(base as any)] : { ...(base as any) };
  for (const [k, v] of Object.entries(override || {})) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      result[k] = deepMerge((result as any)[k] || {}, v as any);
    } else {
      result[k] = v as any;
    }
  }
  return result as T;
}

const BLOCKED_LOCALE_PATH_SEGMENTS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Dotted key (home.goodEvening) is the full app path — ignore type/namespace.
 * Short key (goodEvening) nests under the type namespace (HOME → home).
 */
function resolveLocalePath(
  keyParts: string[],
  namespace: string | undefined,
): string[] | null {
  if (keyParts.length > 1) {
    return keyParts;
  }
  if (namespace) {
    return [namespace, keyParts[0]!];
  }
  return null;
}

/** Prefer `key`, then `title` (Contentful locales alternate field names). */
function resolveTranslationKeyField(
  fields: Record<string, unknown>,
): string | undefined {
  if (typeof fields.key === 'string') {
    return fields.key;
  }
  if (typeof fields.title === 'string') {
    return fields.title;
  }
  return undefined;
}

/**
 * Write a locales entry into the translation tree.
 *
 * Contentful may send:
 * - namespace `home` + key `goodEvening` → home.goodEvening
 * - key `home.goodEvening` (full dotted path) → home.goodEvening
 */
function assignLocaleEntry(
  out: Record<string, unknown>,
  namespace: string | undefined,
  key: string,
  value: string,
): void {
  const keyParts = key
    .split('.')
    .map((part) => part.trim())
    .filter(Boolean);
  if (keyParts.length === 0) {
    return;
  }

  const isDottedKey = keyParts.length > 1;
  const path = resolveLocalePath(keyParts, namespace);
  if (!path || path.some((part) => BLOCKED_LOCALE_PATH_SEGMENTS.has(part))) {
    return;
  }

  let cursor: Record<string, unknown> = out;
  for (let i = 0; i < path.length - 1; i++) {
    const part = path[i]!;
    const existing = cursor[part];
    if (!existing || typeof existing !== 'object' || Array.isArray(existing)) {
      cursor[part] = {};
    }
    cursor = cursor[part] as Record<string, unknown>;
  }

  const leaf = path.at(-1)!;
  // Prefer dotted keys (home.goodMorning) over legacy short keys (goodMorning)
  // when both exist in the same locales payload.
  if (!isDottedKey && typeof cursor[leaf] === 'string') {
    return;
  }
  cursor[leaf] = value;
}

function mapContentfulToTree(raw: any, _locale: Locale): Partial<TranslationKeys> {
  // Supports:
  // 1) One content type per namespace (contentType.sys.id = 'common' | 'auth' | ...)
  // 2) Flat entries with fields: { namespace, key, value }
  // 3) locales model: content_type = 'locales' with fields: { key, value, type -> Entry{ fields.value = namespace } }
  // 4) locales model with 'title' field as alternative to 'key' field
  const out: any = {};
  try {
    const items: any[] = Array.isArray(raw?.items) ? raw.items : [];
    const includes: any[] = Array.isArray(raw?.includes?.Entry) ? raw.includes.Entry : [];
    const includeById: Record<string, any> = {};
    for (const inc of includes) {
      if (inc?.sys?.id) includeById[inc.sys.id] = inc;
    }

    for (const item of items) {
      const nsFromType: string | undefined = item?.sys?.contentType?.sys?.id;
      const fields: Record<string, any> = item?.fields || {};

      // `PROPERTY_DETAILS` → `propertyDetails`, so Contentful values land in the
      // namespace the translation keys actually read.
      const normalizeNamespace = (ns: string | undefined): string | undefined => {
        return ns ? normalizeLocaleNamespace(ns) : undefined;
      };

      // 2) explicit namespace/key/value
      const explicitNs = normalizeNamespace(typeof fields.namespace === 'string' ? fields.namespace : undefined);
      // Support both 'key' and 'title' fields for the translation key
      const explicitKey = resolveTranslationKeyField(fields);
      const explicitVal = typeof fields.value === 'string' ? fields.value : undefined;
      if (explicitNs && explicitKey && typeof explicitVal === 'string') {
        assignLocaleEntry(out, explicitNs, explicitKey, explicitVal);
        continue;
      }

      // 3) locales model with reference-based namespace should take precedence when content type is 'locales'
      // This prevents incorrectly creating a 'locales' namespace bucket.
      if (nsFromType === 'locales') {
        const maybeType = fields.type;
        let namespaceFromRef: string | undefined;

        // Try to get namespace from type reference
        // Check if type is already resolved (inline) or needs to be looked up in includes
        if (maybeType?.fields?.value && typeof maybeType.fields.value === 'string') {
          namespaceFromRef = maybeType.fields.value;
        } else if (maybeType?.sys?.id) {
          const inc = includeById[maybeType.sys.id];
          if (inc?.fields?.value && typeof inc.fields.value === 'string') {
            namespaceFromRef = inc.fields.value;
          }
        }

        namespaceFromRef = normalizeNamespace(namespaceFromRef);

        // Support both 'key' and 'title' fields for the translation key
        const localesKey = resolveTranslationKeyField(fields);
        const localesVal = typeof fields.value === 'string' ? fields.value : undefined;

        // Dotted keys (home.goodEvening) map even if type/namespace is missing.
        if (localesKey && typeof localesVal === 'string' && (namespaceFromRef || localesKey.includes('.'))) {
          assignLocaleEntry(out, namespaceFromRef, localesKey, localesVal);
          continue;
        }

        // Log failed mappings only as warnings
        if (localesKey || localesVal) {
          logger.warn('translationService', 'Failed to map Contentful entry', {
            entryId: item.sys?.id,
            hasNamespace: !!namespaceFromRef,
            hasKey: !!localesKey,
            hasValue: !!localesVal,
            typeRefId: maybeType?.sys?.id,
          });
        }

        // If we couldn't map via reference, skip adding under 'locales'
        continue;
      }

      // 1) namespace from content type id (non-'locales')
      if (nsFromType) {
        const namespace = normalizeNamespace(nsFromType) || nsFromType;
        const target = (out[namespace] = out[namespace] || {});
        for (const [k, v] of Object.entries(fields)) {
          if (typeof v === 'string') target[k] = v;
        }
        continue;
      }

      // 3) locales model with reference-based namespace (fallback path)
      const maybeType = fields.type;
      let namespaceFromRef: string | undefined;
      if (maybeType?.fields?.value && typeof maybeType.fields.value === 'string') {
        namespaceFromRef = maybeType.fields.value;
      } else if (maybeType?.sys?.id && includeById[maybeType.sys.id]?.fields?.value) {
        const inc = includeById[maybeType.sys.id];
        if (typeof inc.fields.value === 'string') namespaceFromRef = inc.fields.value;
      }
      namespaceFromRef = normalizeNamespace(namespaceFromRef);
      // Support both 'key' and 'title' fields for the translation key
      const fallbackKey = resolveTranslationKeyField(fields);
      const fallbackVal = typeof fields.value === 'string' ? fields.value : undefined;
      if (fallbackKey && typeof fallbackVal === 'string' && (namespaceFromRef || fallbackKey.includes('.'))) {
        assignLocaleEntry(out, namespaceFromRef, fallbackKey, fallbackVal);
        continue;
      }
    }

    // Log summary of mapped translations
    const namespaces = Object.keys(out);
    const totalKeys = Object.values(out).reduce((sum: number, ns: any) => sum + Object.keys(ns || {}).length, 0);
    logger.info('translationService', 'mapContentfulToTree summary', {
      namespaces,
      totalKeys,
    });
  } catch (e) {
    logger.warn('translationService', 'mapContentfulToTree failed', e);
  }
  return out as Partial<TranslationKeys>;
}

function storageKey(locale: Locale): string {
  return `i18n:${locale}:v1`;
}

function parseStoredCacheRecord(storedJson: string): CacheRecord | null {
  try {
    return JSON.parse(storedJson) as CacheRecord;
  } catch {
    return null;
  }
}

async function persistTranslationCache(
  locale: Locale,
  record: CacheRecord,
): Promise<void> {
  if (record.etag) {
    metaCache.set(locale, { etag: record.etag, updatedAt: record.updatedAt });
  } else {
    metaCache.set(locale, { updatedAt: record.updatedAt });
  }
  await asyncStorage.setItem(storageKey(locale), JSON.stringify(record));
}

export async function preloadTranslations(locale: Locale): Promise<void> {
  try {
    logger.info('translationService', 'Preloading translations', { locale });

    // In development, always clear cache to ensure fresh Contentful data
    if (isDevelopment) {
      logger.info('translationService', 'Development mode: Clearing translation cache', { locale });
      memoryCache.delete(locale);
      metaCache.delete(locale);
      await asyncStorage.removeItem(storageKey(locale));
    }

    // 1) Hydrate from storage for instant UI (handles fast refresh where memoryCache is cleared)
    const storedJson = await asyncStorage.getItem(storageKey(locale));

    if (storedJson) {
      const stored = parseStoredCacheRecord(storedJson);
      if (!stored) {
        logger.warn('translationService', 'Invalid translation cache JSON', { locale });
        memoryCache.set(locale, bundled(locale));
        restoreWebScalingFromCache(undefined);
        restoreEmiratesIdOtpResendFromCache(undefined);
      } else {
        const cacheAge = Date.now() - stored.updatedAt;
        const cacheAgeMinutes = Math.floor(cacheAge / 60000);
        const bundledBase = bundled(locale);
        const merged = deepMerge(bundledBase, stored.data);
        memoryCache.set(locale, merged);
        if (stored.etag) {
          metaCache.set(locale, { etag: stored.etag, updatedAt: stored.updatedAt });
        } else {
          metaCache.set(locale, { updatedAt: stored.updatedAt });
        }
        restoreWebScalingFromCache(stored.enableWebScaling);
        restoreEmiratesIdOtpResendFromCache(stored.emiratesIdOtpResendSeconds);

        logger.debug('translationService', 'Loaded from cache', {
          locale,
          cacheAgeMinutes,
          hasEtag: !!stored.etag,
          storedNamespaces: Object.keys(stored.data || {}).length,
          mergedNamespaces: Object.keys(merged).length,
          enableWebScaling: stored.enableWebScaling,
          emiratesIdOtpResendSeconds: stored.emiratesIdOtpResendSeconds,
        });
      }
    } else {
      memoryCache.set(locale, bundled(locale));
      restoreWebScalingFromCache(undefined);
      restoreEmiratesIdOtpResendFromCache(undefined);
      logger.debug('translationService', 'No cache found, using bundled translations', { locale });
    }

    // 2) Always revalidate from Contentful on every preload (every page refresh / app start).
    // Cache above is only for instant UI — do not skip the network call based on TTL.
    const firstRun = !storedJson;
    logger.info('translationService', 'Revalidating translations from Contentful', {
      locale,
      firstRun,
      hadCache: Boolean(storedJson),
    });

    if (firstRun) {
      // Block until remote fetched so UI sees Contentful values immediately
      await revalidate(locale);
    } else {
      void revalidate(locale).catch((e) =>
        logger.warn('translationService', 'Revalidate failed', e),
      );
    }
  } catch (error) {
    const errorDetails = error instanceof Error
      ? { message: error.message, stack: error.stack, locale }
      : { error, locale };
    logger.error('translationService', 'preloadTranslations failed', errorDetails);
    if (!memoryCache.get(locale)) memoryCache.set(locale, bundled(locale));
  }
}

async function readCachedLocaleConfig(
  locale: Locale,
): Promise<Pick<CacheRecord, 'enableWebScaling' | 'emiratesIdOtpResendSeconds'>> {
  const storedJson = await asyncStorage.getItem(storageKey(locale));
  if (!storedJson) {
    return {};
  }
  const stored = parseStoredCacheRecord(storedJson);
  return {
    enableWebScaling: stored?.enableWebScaling,
    emiratesIdOtpResendSeconds: stored?.emiratesIdOtpResendSeconds,
  };
}

/** 304 Not Modified: refresh timestamp and keep bundled + existing merge. */
async function persistNotModifiedTranslations(
  locale: Locale,
  etag: string | undefined,
): Promise<void> {
  logger.info('translationService', 'No new data (304 Not Modified)', { locale });
  const existing = memoryCache.get(locale) || bundled(locale);
  const merged = deepMerge(bundled(locale), existing);
  memoryCache.set(locale, merged);
  bumpTranslationRevision();

  const prevLocaleConfig = await readCachedLocaleConfig(locale);
  const record: CacheRecord = {
    updatedAt: Date.now(),
    data: merged,
  };
  if (etag) {
    record.etag = etag;
  }
  if (prevLocaleConfig.enableWebScaling !== undefined) {
    record.enableWebScaling = prevLocaleConfig.enableWebScaling;
  }
  if (prevLocaleConfig.emiratesIdOtpResendSeconds !== undefined) {
    record.emiratesIdOtpResendSeconds = prevLocaleConfig.emiratesIdOtpResendSeconds;
  }
  await persistTranslationCache(locale, record);
}

async function mergeAdhaJourneyIfNeeded(
  locale: Locale,
  base: TranslationKeys,
): Promise<TranslationKeys> {
  const { isAdhaJourney, auth } = useAppStore.getState();
  // Converted leads are not on the ADHA journey, but owned units can still
  // carry `isADHAUnit` + `status`. Those labels use the same tagged locales.
  // Guests own no units, so they never need this copy.
  if (!isAdhaJourney) {
    if (!auth.isAuthenticated) {
      return base;
    }
    return mergeReservationStatusTranslations(locale, base);
  }
  markAdhaReservationStatusCopyPending();
  try {
    return await mergeAdhaJourneyTranslations(locale, base);
  } catch (e) {
    logger.warn('translationService', 'Failed to fetch/merge ADHA translations', {
      platform: Platform.OS,
      locale,
      error: e,
    });
    return base;
  } finally {
    reservationStatusLoadedLocales.add(locale);
    markAdhaReservationStatusCopyReady();
  }
}

function revalidateErrorDetails(error: unknown, locale: Locale) {
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack, locale };
  }
  return { error, locale };
}

async function revalidate(locale: Locale): Promise<void> {
  try {
    const currentMeta = metaCache.get(locale);
    const etag = currentMeta?.etag;
    logger.info('translationService', 'Revalidating translations', { locale, hasEtag: !!etag });
    const result = await fetchAllNamespaces(locale, etag);
    if (result.data == null) {
      await persistNotModifiedTranslations(locale, etag);
      if (!useAppStore.getState().isAdhaJourney) {
        const current = memoryCache.get(locale) || bundled(locale);
        const merged = await mergeReservationStatusTranslations(locale, current);
        memoryCache.set(locale, merged);
        bumpTranslationRevision();
      }
      return;
    }

    const enableWebScaling = applyWebScalingFromLocalesRaw(result.data, locale);
    const emiratesIdOtpResendSeconds = applyEmiratesIdOtpResendFromLocalesRaw(
      result.data,
      locale,
    );

    // Map raw Contentful into partial tree, then merge with fallbacks
    const remotePartial = mapContentfulToTree(result.data, locale);

    // Log what was mapped — full key/value data (not just a namespace/count
    // summary), so device logs (EXPO_PUBLIC_ENABLE_LOGS) show exactly what
    // came from Contentful for this locale, distinct from bundled fallback.
    logger.info('translationService', 'Contentful mapping result', {
      locale,
      mappedNamespaces: Object.keys(remotePartial),
      totalKeys: Object.values(remotePartial).reduce((sum: number, ns: any) => sum + Object.keys(ns || {}).length, 0),
      // data: remotePartial,
    });

    // Merge strategy: bundled translations (base) + Contentful overrides
    // This ensures bundled translations (including new namespaces like 'support') are always included
    // even if Contentful doesn't have them yet
    const bundledBase = deepMerge(bundled('en'), bundled(locale));
    let merged = deepMerge(
      bundledBase,
      remotePartial as Partial<TranslationKeys>
    );
    merged = await mergeAdhaJourneyIfNeeded(locale, merged);

    logger.info('translationService', 'Translations merged and cached', {
      locale,
      bundledNamespaces: Object.keys(bundledBase).length,
      remoteNamespaces: Object.keys(remotePartial).length,
      mergedNamespaces: Object.keys(merged).length,
    });

    // Update caches and persist
    memoryCache.set(locale, merged);
    bumpTranslationRevision();
    const record: CacheRecord = {
      updatedAt: Date.now(),
      data: merged,
      enableWebScaling,
      emiratesIdOtpResendSeconds,
    };
    if (result.etag) {
      record.etag = result.etag;
    }
    await persistTranslationCache(locale, record);
  } catch (error) {
    logger.error(
      'translationService',
      'revalidate failed',
      revalidateErrorDetails(error, locale),
    );
    // Fall back to existing cache or bundled translations
    if (!memoryCache.get(locale)) {
      memoryCache.set(locale, bundled(locale));
      bumpTranslationRevision();
    }
    throw error;
  }
}

export function getTranslations(locale: Locale): TranslationKeys {
  // If memory cache has data, ensure it's merged with bundled to include any new namespaces
  if (memoryCache.has(locale)) {
    const cached = memoryCache.get(locale)!;
    const bundledBase = bundled(locale);
    // Merge strategy: bundled (base) + cached (override) so Contentful/cached values take precedence
    // This ensures Contentful translations override bundled, but bundled provides fallbacks for missing keys
    const merged = deepMerge(bundledBase, cached);
    // Only update if there's a difference (to avoid unnecessary re-renders)
    if (JSON.stringify(merged) !== JSON.stringify(cached)) {
      memoryCache.set(locale, merged);
    }
    return merged;
  }

  // If memory cache is empty (e.g., after fast refresh), return bundled
  // This is a synchronous fallback - async preload should have run, but in case it didn't
  // we still return bundled to avoid blocking the UI
  return bundled(locale);
}

export async function invalidateTranslations(locale: Locale): Promise<void> {
  logger.info('translationService', 'Invalidating translations', { locale });
  memoryCache.delete(locale);
  metaCache.delete(locale);
  await asyncStorage.removeItem(storageKey(locale));
}

export async function invalidateAllTranslations(): Promise<void> {
  logger.info('translationService', 'Invalidating all translations');
  memoryCache.clear();
  metaCache.clear();
  await Promise.all([
    asyncStorage.removeItem(storageKey('en')),
    asyncStorage.removeItem(storageKey('ar')),
  ]);
}

export function getValue(locale: Locale, key: string): string {
  const tree = getTranslations(locale) as any;
  return key.split('.').reduce((acc, part) => (acc ? acc[part] : undefined), tree) ?? key;
}

export async function fetchKey(locale: Locale, key: string, options?: { forceNetwork?: boolean }): Promise<string> {
  try {
    const meta = metaCache.get(locale);
    const expired = !meta || Date.now() - meta.updatedAt > CACHE_TTL_MS;
    if (options?.forceNetwork || expired) {
      await revalidate(locale);
    } else if (!memoryCache.get(locale)) {
      await preloadTranslations(locale);
    }
  } catch (e) {
    logger.warn('translationService', 'fetchKey fallback to cache/bundled', e);
  }
  return getValue(locale, key);
}



/**
 * Returns false when Contentful could not be reached (nothing was changed).
 * Pull-to-refresh: re-check Contentful for every translated string (etag-aware),
 * then re-fetch the ADHA journey copy (incl. reservation-status messages), which
 * the general revalidate skips when the shared locales are unchanged.
 * Unlike loadAdhaTranslationsOnly, the status copy is not reset to "pending",
 * so the UI keeps showing the current text until the new text arrives.
 */
export async function refreshContentfulTranslations(locale: Locale): Promise<boolean> {
  try {
    await revalidate(locale);
  } catch (error) {
    // Offline / Contentful down: keep the current copy and report it, so callers
    // do not go on to reload (and possibly blank) other CMS content.
    logger.warn('translationService', 'refreshContentfulTranslations: revalidate failed', {
      locale,
      error,
    });
    return false;
  }
  if (!useAppStore.getState().isAdhaJourney) {
    return true;
  }
  try {
    const current = memoryCache.get(locale) || deepMerge(bundled('en'), bundled(locale));
    memoryCache.set(locale, await mergeAdhaJourneyTranslations(locale, current));
    bumpTranslationRevision();
  } catch (error) {
    // Keep the current copy on screen when the refresh fails.
    logger.warn('translationService', 'refreshContentfulTranslations: ADHA copy failed', {
      locale,
      error,
    });
  }
  return true;
}

export async function loadAdhaTranslationsOnly(locale: Locale): Promise<void> {
  markAdhaReservationStatusCopyPending();
  try {
    const existingCache =
      memoryCache.get(locale) || deepMerge(bundled('en'), bundled(locale));
    // Status messages (tag `adhaReservationStatus`) load with general ADHA locales.
    // UI shimmers until this settles, then CF status or the raw API status.
    const merged = await mergeAdhaJourneyTranslations(locale, existingCache);
    memoryCache.set(locale, merged);
  } catch {
    // Silent fail — ready flag still flips in finally so UI can leave shimmer
  } finally {
    reservationStatusLoadedLocales.add(locale);
    markAdhaReservationStatusCopyReady();
  }
}

/**
 * Load Contentful `adhaReservationStatus` copy for a non-ADHA session
 * (converted lead with `isADHAUnit`). Same keys the ADHA journey merges.
 */
export function hasAdhaReservationStatusCopy(locale: Locale): boolean {
  return reservationStatusLoadedLocales.has(locale);
}

export function ensureAdhaReservationStatusCopy(locale: Locale): Promise<void> {
  if (reservationStatusLoadedLocales.has(locale)) {
    return Promise.resolve();
  }

  return loadReservationStatusTranslations(locale).then((slice) => {
    const latest =
      memoryCache.get(locale) || deepMerge(bundled('en'), bundled(locale));
    memoryCache.set(locale, deepMerge(latest, slice));
    reservationStatusLoadedLocales.add(locale);
    markAdhaReservationStatusCopyReady();
    bumpTranslationRevision();
  });
}
